import moment from 'moment';
import { Month } from '@commenda/types';
import { NonTimezoneSensitiveDate } from './non-time-zone-sensitive-date';

export const getRelativeDateDisplay = (
  dateString: string
): 'Today' | 'Tomorrow' | string => {
  // Create NonTimezoneSensitiveDate instances for today and tomorrow
  const today = NonTimezoneSensitiveDate.now();
  const tomorrow = today.add(1, 'days');

  // Create a NonTimezoneSensitiveDate instance for the input date
  const date = NonTimezoneSensitiveDate.fromISO8601String(dateString);

  // Compare the dates using diff
  if (today.diff(date) === 0) return 'Today';
  if (tomorrow.diff(date) === 0) return 'Tomorrow';

  // Return the formatted date if it's neither today nor tomorrow
  return date.format('MMM DD, YYYY');
};

const getMonthNameByNumber = (monthNumber: number): string => {
  const date = new Date();
  date.setMonth(monthNumber - 1);

  return date.toLocaleString('en-US', { month: 'long' });
};

type PrintDateStringOptions = {
  shortMonthName?: boolean; // Default false.
  withTime?: boolean; // Default false.
};

function printDateString(
  dateString: string | Date,
  options?: PrintDateStringOptions
) {
  if (!dateString) {
    return '';
  }
  // Handle janky input from misbehaving callers:
  if (dateString instanceof Date) {
    // eslint-disable-next-line no-param-reassign
    dateString = (dateString as Date).toISOString();
  }
  // Define an array of month names
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Extract the year, month, and day from the ISO 8601 date string
  const year = dateString?.substring(0, 4);
  const fullMonth = months[parseInt(dateString?.substring(5, 7), 10) - 1]; // Convert to zero-based index
  const month = options?.shortMonthName ? fullMonth.substring(0, 3) : fullMonth;
  const day = dateString?.substring(8, 10);

  const timeStamp = new Date(dateString).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Format the date
  return `${month} ${parseInt(day, 10)}, ${year} ${
    options?.withTime ? `at ${timeStamp}` : ''
  }`;
}

function timeAgo(date: string) {
  if (!date) {
    return '';
  }
  if (Number.isNaN(Date.parse(date))) {
    return '';
  }
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();

  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const weeks = Math.round(days / 7);

  // Format date for return
  const formattedDate = printDateString(date);

  if (seconds < 60) {
    if (seconds < 10) return 'Just now';

    if (seconds === 1) {
      return '1 second ago';
    }
    return `${seconds} seconds ago`;
  }
  if (minutes < 60) {
    if (minutes === 1) {
      return '1 minute ago';
    }
    return `${minutes} minutes ago`;
  }
  if (hours < 24) {
    if (hours === 1) {
      return '1 hour ago';
    }
    return `${hours} hours ago`;
  }
  if (days < 7) {
    if (days === 1) {
      return 'Yesterday';
    }
    return `${days} days ago`;
  }
  if (weeks < 4) {
    if (weeks === 1) {
      return 'Last week';
    }
    return `${weeks} weeks ago`;
  }
  // For longer periods, return the formatted date
  return formattedDate;
}

// Human readable description to answer the question 'what time did this happen?'.
function readableTimestamp(date: Date | string) {
  const inputDate = new Date(date);

  return inputDate.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const parseDateISO8601 = (dateString: string) => {
  return new Date(dateString);
};

const compareDatesISO8601 = (dateString1, dateString2) => {
  const date1 = parseDateISO8601(dateString1);
  const date2 = parseDateISO8601(dateString2);

  return date1.getTime() - date2.getTime();
};

/** Compares two date strings chronologically for use in sort functions. Null/undefined always sorts to the bottom. */
export function compareDatesChronologically(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  const dateA = a ? new Date(a).getTime() : null;
  const dateB = b ? new Date(b).getTime() : null;
  if (dateA === null && dateB === null) return 0;
  if (dateA === null) return 1;
  if (dateB === null) return -1;
  return dateA - dateB;
}

const isLeapYear = (year: number) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const monthsWith31 = [1, 3, 5, 7, 8, 10, 12];
const monthsWith30 = [4, 6, 9, 11];

const getDaysInMonth = (month: number, year: number) => {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if (monthsWith31.includes(month)) return 31;
  if (monthsWith30.includes(month)) return 30;
  return 0;
};

function getLastNYears(n: number) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < n; i += 1) {
    years.push((currentYear - i).toString());
  }
  return years.reverse();
}

export const MonthIndexOfMonth: Record<Month, number> = {
  [Month.JAN]: 1,
  [Month.FEB]: 2,
  [Month.MAR]: 3,
  [Month.APR]: 4,
  [Month.MAY]: 5,
  [Month.JUN]: 6,
  [Month.JUL]: 7,
  [Month.AUG]: 8,
  [Month.SEP]: 9,
  [Month.OCT]: 10,
  [Month.NOV]: 11,
  [Month.DEC]: 12,
};

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Last calendar day of the given month in local time.
 * @param year - Full year (e.g. 2026)
 * @param month - 0-based month (0 = January), same as {@link Date#getMonth}
 */
export function getEndOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

export const isWithinDateRange = (
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number
): boolean => {
  const today = NonTimezoneSensitiveDate.now();
  const currentYear = today.getYear();

  const startDate = new NonTimezoneSensitiveDate(
    currentYear,
    startMonth,
    startDay
  );
  const endDate = new NonTimezoneSensitiveDate(currentYear, endMonth, endDay);

  return today.gte(startDate) && today.lte(endDate);
};

export const MS_PER_SECOND = 1000;
export const MS_PER_MINUTE = 60 * MS_PER_SECOND;
export const MS_PER_HOUR = 60 * MS_PER_MINUTE;
export const MS_PER_DAY = 24 * MS_PER_HOUR;

export function formatDuration(ms: number): string {
  if (ms <= 0) return '0m';

  const days = Math.floor(ms / MS_PER_DAY);
  const hours = Math.floor((ms % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 && days === 0) {
    parts.push(`${minutes}m`);
  }

  return parts.length > 0 ? parts.join(' ') : '0m';
}

/**
 * Long-form en-US date for filenames — e.g. `May 5, 2026`. Picked once so
 * every "report"-style download in the app reads consistently.
 */
export const formatLongDate = (date: Date = new Date()): string =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** TZ-safe `MMM D, YYYY`: reads the literal Y/M/D parts (no TZ shift) and formats via moment. Null on bad input. */
export const formatIsoDateShort = (iso?: string | null): string | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new NonTimezoneSensitiveDate(y, m, d).format('MMM D, YYYY');
};

/** `MMM D, YYYY h:mm A` from an ISO timestamp; null on missing/invalid input. */
export const formatIsoDateTimeShort = (iso?: string | null): string | null => {
  if (!iso) return null;
  const parsed = moment(iso);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : null;
};

/**
 * The `YYYY-MM-DD` portion of an ISO string, taken directly (no `Date` parse)
 * so it can't shift a day across timezones.
 */
export const isoDateOnly = (iso?: string | null): string | undefined =>
  iso ? iso.slice(0, 10) : undefined;

/**
 * True only for a real `YYYY-MM-DD` calendar date — rejects out-of-range parts that
 * `new Date` would silently roll over (e.g. "2026-02-30" → Mar 2, "2026-13-01").
 */
export const isValidCalendarDate = (iso?: string | null): boolean => {
  if (!iso) return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return false;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const dt = new Date(Date.UTC(year, month - 1, day));
  return (
    dt.getUTCFullYear() === year &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
};

export {
  compareDatesISO8601,
  getDaysInMonth,
  getLastNYears,
  getMonthNameByNumber,
  printDateString,
  readableTimestamp,
  timeAgo,
};
